
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import CarCard from '@/components/CarCard';
import { useCar } from '@/contexts/CarContext';
import { useCarSelectionContext } from '@/contexts/CarSelectionContext';
import { Plus, Car as CarIcon } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { useI18n } from '@/i18n/LanguageContext';

const CarsPage = () => {
  const { cars } = useCar();
  const { selectedCarId, selectCar, mainCarId, setMainCar } = useCarSelectionContext();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [targetMainId, setTargetMainId] = useState<string | null>(null);
  const [prevMainId, setPrevMainId] = useState<string | null>(null);

  // Main car first in the list
  const carsSorted = useMemo(() => {
    if (!mainCarId || cars.length <= 1) return cars;
    const main = cars.find(c => c.id === mainCarId);
    const rest = cars.filter(c => c.id !== mainCarId);
    return main ? [main, ...rest] : cars;
  }, [cars, mainCarId]);

  useEffect(() => {
    // Redirect to CarDetailPage when no cars exist (for new users)
    if (cars.length === 0) {
      navigate('/cars/new', { replace: true });
    }
  }, [cars.length, navigate]);
  
  const handleCarSelect = (carId: string) => {
    selectCar(carId);
    navigate(`/cars/${carId}`);
  };
  
  // removed switching main car from this page for now
  
  return (
    <PageLayout title={t('cars.title')} showBackButton onBack={() => navigate(-1)}>
      <div className="space-y-4">
        {carsSorted.length > 0 ? (
          <div className="space-y-4">
            <div className="space-y-4">
              {carsSorted.map((car) => (
                <div key={car.id} className="relative">

                  <CarCard 
                    car={car} 
                    onClick={() => handleCarSelect(car.id)} 
                    selected={car.id === selectedCarId}
                    isMainCar={car.id === mainCarId}
                    onRequestSetMain={() => {
                      setPrevMainId(mainCarId || null);
                      setTargetMainId(car.id);
                      setConfirmOpen(true);
                    }}
                    onRequestOpenDetails={() => handleCarSelect(car.id)}
                  />
                </div>
              ))}
            </div>
            
            <Button
              variant="secondary"
              onClick={() => navigate('/cars/add')}
              className="w-full flex items-center justify-center mt-6"
            >
              <Plus size={20} className="mr-2" />
              {t('cars.addCar')}
            </Button>
          </div>
        ) : (
          <div className="w-full min-h-[60vh] bg-gray-50 rounded-2xl flex items-start justify-center px-4 pt-10 pb-24">
            <div className="bg-white rounded-2xl shadow-sm p-6 w-full max-w-sm text-center space-y-5">
              {/* Icon */}
              <div className="flex justify-center">
                <CarIcon size={72} className="text-gray-300" />
              </div>

              {/* Title */}
              <h2 
                className="text-xl font-semibold text-gray-900"
                style={{ fontFamily: 'Lato' }}
              >
                {t('cars.noCars')}
              </h2>

              {/* Description */}
              <p 
                className="text-sm text-gray-500 leading-relaxed"
                style={{ fontFamily: 'Lato' }}
              >
                {t('cars.emptyStateDescription')}
              </p>

              {/* CTA Button */}
              <Button
                onClick={() => navigate('/cars/add')}
                className="w-full py-3 rounded-xl bg-[#e7fe52] text-gray-900 font-semibold text-sm shadow hover:opacity-90 flex items-center justify-center"
                style={{ fontFamily: 'Lato' }}
              >
                <Plus size={20} className="mr-2" />
                {t('cars.addCar')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Confirm dialog for setting main car */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('cars.setMainConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('cars.setMainConfirmDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-row items-center gap-2 justify-end">
            <AlertDialogCancel className="mt-0">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!targetMainId) return;
                setMainCar(targetMainId);
                setConfirmOpen(false);
                toast({ title: t('cars.mainCarUpdated'), description: t('cars.mainCarUpdatedDesc') });
                toast({
                  title: t('cars.undoChange'),
                  action: (
                    <button
                      onClick={() => {
                        if (prevMainId) setMainCar(prevMainId);
                      }}
                      className="ml-2 underline"
                    >
                      {t('common.undo')}
                    </button>
                  ) as any,
                });
              }}
            >
              {t('cars.setAsMainCar')}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>

    </PageLayout>
  );
};

export default CarsPage;
